import { motion } from 'motion/react';
import {
  TrendingUp,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  Search,
  ShoppingBag,
  Briefcase,
  Share2
} from 'lucide-react';
import { cn } from '../../utils/helpers';
import { useTranslation } from '../../hooks/useTranslation';
import { useLocale } from '../../hooks/useLocale';

export default function Dashboard() {
  const { t } = useTranslation();
  const { formatCurrency, formatNumber, formatMediumDate } = useLocale();

  const stats = [
    { label: t('dashboard.walletBalance'), value: formatCurrency(12450), change: '+12.5%', icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400' },
    { label: t('dashboard.activeOrders'), value: formatNumber(24), change: t('dashboard.newCount', { n: 4 }), icon: ShoppingBag, color: 'text-blue-600 dark:text-blue-400' },
    { label: t('dashboard.jobApplications'), value: formatNumber(12), change: t('dashboard.pendingCount', { n: 2 }), icon: Briefcase, color: 'text-amber-600 dark:text-amber-400' },
    { label: t('dashboard.socialReach'), value: formatNumber(1200000, { notation: 'compact' }), change: '+5.2%', icon: Share2, color: 'text-purple-600 dark:text-purple-400' },
  ];

  const transactions = [
    { name: 'Apple Store', type: t('dashboard.purchase'), amount: -1299, date: new Date(2023, 9, 24), status: t('dashboard.completed') },
    { name: 'Salary Deposit', type: t('dashboard.income'), amount: 4500, date: new Date(2023, 9, 22), status: t('dashboard.completed') },
    { name: 'Netflix', type: t('dashboard.subscription'), amount: -15.99, date: new Date(2023, 9, 20), status: t('dashboard.pending') },
    { name: 'Stripe Payout', type: t('dashboard.transfer'), amount: 850, date: new Date(2023, 9, 18), status: t('dashboard.completed') },
  ];

  const quickActions = [
    { label: t('dashboard.sendMoney'), icon: ArrowUpRight, color: 'bg-neutral-900 text-white' },
    { label: t('dashboard.topUp'), icon: Plus, color: 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800' },
    { label: t('dashboard.postUpdate'), icon: Share2, color: 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800' },
    { label: t('dashboard.addProduct'), icon: ShoppingBag, color: 'bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800' },
  ];

  return (
    <div className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.welcome', { name: 'Alex' })}</h1>
          <p className="text-neutral-500 dark:text-neutral-400">{t('dashboard.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors">
            <Search className="w-4 h-4" />
            {t('common.search')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white rounded-xl text-sm font-medium hover:bg-neutral-800 transition-colors shadow-lg shadow-neutral-900/20">
            <Plus className="w-4 h-4" />
            {t('dashboard.newAction')}
          </button>
        </div>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white dark:bg-neutral-900 p-6 rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={cn("p-2 rounded-xl bg-neutral-50 dark:bg-neutral-950", stat.color)}>
                <stat.icon className="w-5 h-5" />
              </div>
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded-lg">
                {stat.change}
              </span>
            </div>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 font-medium">{stat.label}</p>
            <h3 className="text-2xl font-bold mt-1">{stat.value}</h3>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Transactions */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{t('dashboard.recentTransactions')}</h2>
            <button className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50">{t('common.viewAll')}</button>
          </div>
          <div className="bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm dark:shadow-none">
            {transactions.map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-4 hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-colors border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center",
                    tx.amount > 0 ? "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400" : "bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300"
                  )}>
                    {tx.amount > 0 ? <ArrowDownLeft className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-semibold">{tx.name}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">{tx.type} • {formatMediumDate(tx.date)}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={cn("font-bold", tx.amount > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-neutral-900 dark:text-neutral-50")}>
                    {tx.amount > 0 ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">{tx.status}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions / Activity */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-2 gap-4">
            {quickActions.map((action) => (
              <button key={action.label} className={cn(
                "flex flex-col items-center justify-center gap-3 p-6 rounded-3xl transition-all hover:scale-[1.02] active:scale-[0.98]",
                action.color
              )}>
                <action.icon className="w-6 h-6" />
                <span className="text-xs font-bold">{action.label}</span>
              </button>
            ))}
          </div>

          <div className="bg-neutral-900 rounded-3xl p-6 text-white overflow-hidden relative">
            <div className="relative z-10">
              <h3 className="text-lg font-bold mb-2">{t('dashboard.upgradeToPro')}</h3>
              <p className="text-neutral-400 dark:text-neutral-500 text-xs mb-4">{t('dashboard.upgradeDesc')}</p>
              <button className="bg-white text-neutral-900 px-4 py-2 rounded-xl text-xs font-bold hover:bg-neutral-100 transition-colors">
                {t('common.learnMore')}
              </button>
            </div>
            <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
